import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { loginSchema, type LoginInput } from '@lms/shared';
import { useAuthStore } from '@/stores/auth';
import { toApiError } from '@/lib/api';
import { loginDestination } from '@/lib/loginDestination';
import { AuthCard, FieldLabel } from '@/components/auth/AuthCard';
import { SsoButtons, ssoErrorMessage } from '@/components/auth/SsoButtons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const clearSignedOut = useAuthStore((s) => s.clearSignedOut);

  /**
   * A failed single sign-on comes back as `?error=<code>` on this page, since
   * the whole exchange is a chain of redirects with nowhere else to put it.
   * Seeded into the same banner the password form uses, so there is one place
   * on the card where sign-in problems appear.
   */
  const [searchParams] = useSearchParams();
  const [serverError, setServerError] = useState<string | null>(() =>
    ssoErrorMessage(searchParams.get('error')),
  );
  const [showPassword, setShowPassword] = useState(false);

  /**
   * The sign-out flag has done its job by the time this page renders — the
   * guard has already decided not to record a destination.
   *
   * Clearing it here keeps the flag to the one redirect it was for. Leaving it
   * set would mean that somebody who signs out and then follows a deep link
   * still lands on the dashboard rather than the link they opened.
   */
  useEffect(() => {
    clearSignedOut();
  }, [clearSignedOut]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (values: LoginInput) => {
    setServerError(null);
    try {
      const user = await login(values.email, values.password);
      /**
       * The guard records a destination only when the session ended on its
       * own; a deliberate sign-out leaves nothing, which lands here on the
       * dashboard. See `loginDestination` for the rules.
       */
      navigate(
        loginDestination({
          mustChangePassword: user.mustChangePassword,
          from: (location.state as { from?: string } | null)?.from,
        }),
        { replace: true },
      );
    } catch (error) {
      setServerError(toApiError(error).message);
    }
  };

  return (
    /*
      A fixed-height page, not a minimum-height one.

      `h-screen` plus `overflow-hidden` is what stops the window scrolling.
      The form column then takes `overflow-y-auto` of its own, so on a short
      window the card scrolls INSIDE its column rather than being clipped —
      without that, "non-scrollable" would mean somebody on a laptop could not
      reach the Continue button at all.
    */
    <div className="grid h-screen overflow-hidden lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      {/*
        Brand panel — hidden on small screens so the form gets full width.

        Colours here are literal, not tokens. It is a fixed light surface
        carrying a light illustration, so it does not follow the theme — and
        `text-muted-foreground` on it would turn pale-on-pale the moment
        somebody signed out with dark mode on.
      */}
      {/* `justify-center`, now that the copyright line is gone — with one
          block left, `justify-between` simply pinned it to the top and left
          the rest of the panel empty below the artwork. */}
      <div className="relative hidden overflow-hidden bg-[#F7F6FD] lg:flex lg:flex-col lg:justify-center lg:p-12 xl:p-14">
        {/* A soft bloom under the illustration, so its edge has somewhere to
            dissolve into instead of ending on the flat panel colour. Biased
            left and low to sit under where the artwork actually is. */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(55% 45% at 38% 72%, rgba(139,116,240,0.17) 0%, rgba(139,116,240,0) 72%)',
          }}
          aria-hidden
        />

        {/*
          The identity and the headline as ONE group, a fixed 0.5cm apart.

          The panel is `justify-between`, which distributes leftover height
          between its children — so with the logo, the headline and the
          copyright as three separate children, the space under the logo was
          not a spacing decision at all. It was whatever was left after the
          artwork and the footer had taken theirs, and it changed with the
          window. Grouping the two makes that gap a fixed measurement, and
          leaves `justify-between` to do the one job it is good at: holding the
          copyright to the bottom edge.
        */}
        <div className="relative flex flex-col gap-[0.5cm]">
          {/*
            A 34px tile, not 44.

            At the larger size the mark competed with the headline for first
            look, which inverts the order — the identity is a signature on the
            page, and the sentence is the thing being said. Text scales with it
            so the block stays one unit rather than a small tile beside big type.

            Both gaps are written in cm because that is the unit they were
            specified in; a Tailwind step would round them and lose the intent.
          */}
          <div className="flex items-center gap-[0.25cm]">
            <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-white shadow-sm ring-1 ring-black/[0.06]">
              <img src="/petpooja-mark.png" alt="" className="h-[21px] w-[21px] object-contain" />
            </span>
            <div className="leading-[1.25]">
              <p className="text-[13.5px] font-semibold tracking-[-0.01em] text-neutral-900">
                Petpooja
              </p>
              <p className="text-[11px] text-neutral-500">Learning Management System</p>
            </div>
          </div>

          {/*
            Headline and artwork are one block, in normal flow.

            The previous cut had the illustration absolutely positioned in the
            corner, which is what put the copy on top of the character — an
            absolute element cannot make room for anything, so the two simply
            occupied the same space. Stacked, they cannot collide at any height.
          */}
          <div>
            <h1 className="max-w-[15ch] font-display text-[2.9rem] leading-[1.12] tracking-[-0.005em] text-neutral-900 xl:text-[3.1rem]">
              Everyone knows{' '}
              {/* One italic word carries the emphasis. Spreading it across the
                  line would leave nothing stressed at all. */}
              <em className="italic text-brand-600">exactly</em> what to learn next.
            </h1>

            {/*
              `hero-art.png`, not `hero-login.png` — the other cut has
              "Learn. Grow. Succeed." painted into it, and two headlines on one
              panel means neither is the headline.

              Sits in the same block as the headline, so its left edge is the
              headline's left edge. Centring it in the panel would leave the
              artwork visibly off-axis from the type directly above it.
            */}
            {/* Capped against the viewport, not just the column. The panel no
                longer scrolls, so on a short window an artwork sized only in
                width would push the headline off the top edge. */}
            <img
              src="/hero-art.png"
              alt=""
              className="mt-10 max-h-[42vh] w-full max-w-[30rem] object-contain object-left"
            />
          </div>
        </div>
      </div>

      {/*
        Form panel.

        A faint tinted ground, so the white card reads as lifted rather than as
        the page itself. Below `lg` the brand panel is hidden and this is the
        whole screen, which is exactly when the card shape earns its keep.
      */}
      <div className="flex items-center justify-center overflow-y-auto bg-brand-50/40 px-5 py-10 sm:px-12">
        {/* 400, not 440 — the reference card is narrower, and at 440 the
            fields ran wide enough that the eye had to travel back across an
            empty gap to reach the next label. */}
        <div className="w-full max-w-[400px]">
          {/* The brand panel is hidden below lg, so this carries the identity
              on a phone. Same 34px block, same proportions. */}
          <div className="mb-6 flex items-center gap-[0.25cm] lg:hidden">
            <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-card shadow-sm ring-1 ring-black/[0.06]">
              <img src="/petpooja-mark.png" alt="" className="h-[21px] w-[21px] object-contain" />
            </span>
            <div className="leading-[1.25]">
              <p className="text-[13.5px] font-semibold tracking-[-0.01em]">Petpooja</p>
              <p className="text-[11px] text-muted-foreground">Learning Management System</p>
            </div>
          </div>

          <AuthCard
            title="Let's get started"
            /* Says nothing about Google: the button above may not be there,
               and a subtitle offering a control that is absent is worse than
               one that offers nothing. */
            subtitle="Sign in with the work account your administrator registered"
          >
            {/* Renders nothing unless this deployment has SSO credentials. */}
            <SsoButtons />

            {serverError && (
              <div
                role="alert"
                className="mb-5 flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-[13px] text-destructive"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>{serverError}</span>
              </div>
            )}

            {/*
              Visible labels, each with the required asterisk.

              These were `sr-only` — the placeholder alone carried the field
              name, which gave a cleaner edge but left nothing on screen once
              somebody started typing. The template names its fields, and a
              label that is actually visible is the better of the two anyway.
            */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="email">
                  Email
                </FieldLabel>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="ex: you@petpooja.com"
                  className="h-[42px] rounded-xl px-3.5 text-[14px]"
                  invalid={Boolean(errors.email)}
                  aria-required="true"
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  {...register('email')}
                />
                {errors.email && (
                  <p id="email-error" className="text-[12.5px] text-destructive">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-baseline justify-between gap-3">
                  <FieldLabel htmlFor="password">
                    Password
                  </FieldLabel>
                  {/* Beside its own field now that the fields carry labels —
                      it belongs to the password, not to the form as a whole. */}
                  <Link
                    to="/forgot-password"
                    className="text-[12.5px] text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className="h-[42px] rounded-xl px-3.5 pr-11 text-[14px]"
                    invalid={Boolean(errors.password)}
                    aria-required="true"
                    aria-describedby={errors.password ? 'password-error' : undefined}
                    {...register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-0 top-0 flex h-[42px] w-11 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:text-foreground"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p id="password-error" className="text-[12.5px] text-destructive">
                    {errors.password.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="mt-1 h-[44px] w-full rounded-xl text-[14px]"
                loading={isSubmitting}
              >
                {isSubmitting ? 'Signing in…' : 'Continue'}
                {!isSubmitting && <ArrowRight className="h-4 w-4" />}
              </Button>
            </form>

            {/*
              One centred line, like the reference's footer.

              This was a filled box running four lines, which gave a note about
              an edge case more weight on the card than the sign-in button. The
              sentence is the same; it now sits where a footnote belongs.
            */}
            <p className="mt-6 border-t pt-5 text-center text-[12.5px] leading-[1.6] text-muted-foreground">
              Can't get in?{' '}
              <span className="font-semibold text-foreground">
                Your administrator can reset your password
              </span>{' '}
              — no request form, no waiting on email.
            </p>
          </AuthCard>
        </div>
      </div>
    </div>
  );
}

